// Groups anomaly readings into INCIDENTS (one real-world situation = one card) and ranks them by Urgency.

// flood, landslide and storm readings at one place are the same weather event: one incident, many hazard tags
const FAMILY = { flood: "hydro", landslide: "hydro", storm: "hydro" };
const LINK_KM = { hydro: 400, fire: 150, seismic: 150, tsunami: 500, volcano: 100 };
const LABEL = {
  hydro: "Storm & rain event", fire: "Fire complex", seismic: "Seismic unrest", storm: "Storm system",
  tsunami: "Tsunami threat", volcano: "Volcanic unrest",
};

const km = (a, b) => {
  const r = Math.PI / 180;
  const h = Math.sin(((b.lat - a.lat) * r) / 2) ** 2 +
    Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(((b.lon - a.lon) * r) / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(h));
};

const isWarning = (m = {}) => [m.rain_level, m.fire_level, m.dam_level, m.flash_level].includes("warning");

// One reading's contribution; the incident takes the strongest member plus breadth / novelty bonuses.
// Weights (max ≈ 100): impact 55 · severity 15 · warning level 8 · people 6 · rising 4 (+ ahead 7, breadth 8 per incident)
const memberScore = (o) => {
  const people = o.impact?.people_at_risk || o.exposure?.population || 0;
  return 0.55 * (o.impact?.score ?? 30) + Math.min(15, (o.deviation_sigma || 0) * 1.2) + (isWarning(o.metrics) ? 8 : 0) +
    Math.max(0, Math.min(6, 2 * (Math.log10(people + 1) - 4))) +
    (["rising", "accelerating"].includes(o.forecast?.trajectory) ? 4 : 0);
};

const place = (o) => (o.region || "").split(" · ")[0].replace(/^near /, "");

export function buildIncidents(observations) {
  const flagged = observations.filter((o) => o.triage_status !== "nominal" && (o.feed?.tier === "main" || o.feed?.tier === "monitoring"));
  const groups = [];
  for (const o of flagged) {
    const fam = FAMILY[o.hazard] || o.hazard;
    const link = LINK_KM[fam] || 200;
    const hits = groups.filter((g) => g.family === fam && g.members.some((m) => km(m, o) <= link));
    if (!hits.length) groups.push({ family: fam, members: [o] });
    else {
      hits[0].members.push(o);
      for (const g of hits.slice(1)) {         // o bridges several groups: merge them
        hits[0].members.push(...g.members);
        groups.splice(groups.indexOf(g), 1);
      }
    }
  }
  return groups
    .map((g) => {
      const ms = [...g.members].sort((a, b) => memberScore(b) - memberScore(a));
      const lead = ms[0];
      const countries = [...new Set(ms.map((m) => m.country).filter(Boolean))];
      const unexplained = ms.filter((m) => m.triage_status === "unexplained_anomaly").length;
      const ahead = unexplained === ms.length;
      const people = Math.max(0, ...ms.map((m) => m.impact?.people_at_risk || m.exposure?.population || 0));
      const urgency = Math.round(Math.min(100, memberScore(lead) + (ahead ? 7 : 0) + Math.min(8, 2.5 * Math.log2(ms.length))));
      const byHazard = ms.reduce((a, m) => ({ ...a, [m.hazard]: (a[m.hazard] || 0) + 1 }), {});
      const lats = ms.map((m) => m.lat);
      const tier = ms.some((m) => (m.feed?.tier || "main") === "main") ? "main" : "monitoring";
      const storm = ms.map((m) => m.storm_link).filter(Boolean).sort((a, b) => a.distance_km - b.distance_km)[0] || null;
      const confirmed = ms.map((m) => m.confirmed).filter(Boolean).sort((a, b) => (b.lead_hours || 0) - (a.lead_hours || 0))[0] || null;
      const reasons = [...new Set(ms.flatMap((m) => m.feed?.reasons || []))];
      const BAND_RANK = { rule_alert: 3, alerted: 2, listed: 1, internal: 0 };
      const band = ms.map((m) => m.feed?.band || "internal").sort((a, b) => BAND_RANK[b] - BAND_RANK[a])[0];
      const conf = lead.feed ? { value: lead.feed.confidence, source: lead.feed.confidence_source } : null;
      const sarVerified = ms.some((m) => m.kind === "landslide_dam" && m.metrics?.dam_level === "warning");
      const lons = ms.map((m) => m.lon);
      return {
        id: `inc-${lead.uid}`, family: g.family, lead, members: ms, tier, storm, confirmed, reasons, band, conf, sarVerified,
        hazards: Object.keys(byHazard), countries, byHazard, people, urgency, ahead,
        unexplained, warning: ms.some((m) => isWarning(m.metrics)),
        title: ms.length > 1
          ? `${storm ? storm.label : LABEL[g.family] || "Incident"} · ${countries.slice(0, 2).join(", ") || place(lead)}` : lead.region,
        bounds: [[Math.min(...lats) - 0.5, Math.min(...lons) - 0.5], [Math.max(...lats) + 0.5, Math.max(...lons) + 0.5]],
      };
    })
    .sort((a, b) => b.urgency - a.urgency);
}

export const TOP_N = 10;
